import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Input from "../Utils/Input";
import {
  PASSWORD_POLICY_HINT,
  passwordComplexitySchema,
} from "../../Utils/passwordPolicy";
import { logout, postChangePasswordAsync } from "../../Redux/AuthSlice";
import messageSlice from "../../Redux/MessageSlice";
import { clearAuthUserData } from "../../Utils/authSession";

export default function ForcedPasswordChangeModal() {
  const [loading, setLoading] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialValues = {
    oldPassword: "",
    newPassword: "",
    confNewPassword: "",
  };

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string().required("This field is required!"),
    newPassword: passwordComplexitySchema,
    confNewPassword: Yup.string()
      .oneOf(
        [Yup.ref("newPassword"), null],
        "New Password and Confirm Password Must Match"
      )
      .required("This field is required!"),
  });

  const handleLogout = () => {
    setLoggingOut(true);
    dispatch(logout())
      .unwrap()
      .finally(() => {
        clearAuthUserData();
        navigate("/");
        window.location.reload();
      });
  };

  const handleChange = (formValue) => {
    setLoading(true);
    dispatch(postChangePasswordAsync({ payload: formValue }))
      .unwrap()
      .then(() => {
        dispatch(messageSlice.actions.clearMessage());
        clearAuthUserData();
        toast.success(
          "Password updated successfully. Please sign in with your new password."
        );
        navigate("/");
        window.location.reload();
      })
      .catch((error) => {
        const msg =
          (typeof error === "string" && error) ||
          error?.message ||
          "Unable to change password. Please try again.";
        toast.error(msg, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        setLoading(false);
      });
  };

  return (
    <div className='fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4'>
      <div
        className='w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl'
        role='dialog'
        aria-modal='true'
        aria-labelledby='forced-password-title'
      >
        <h2
          id='forced-password-title'
          className='text-xl font-semibold text-slate-900'
        >
          Password update required
        </h2>
        <p className='mt-2 text-sm text-slate-600'>
          Your password has expired. Please set a new password to continue.
        </p>
        <p className='mt-2 text-xs text-slate-500'>{PASSWORD_POLICY_HINT}</p>
        <p className='mt-2 text-xs text-slate-500'>
          Forgot your current password? Log out and use Forgot Password from the
          login page.
        </p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleChange}
        >
          <Form className='mt-5 space-y-4'>
            <Input
              type='password'
              label='Current Password'
              id='oldPassword'
              name='oldPassword'
            />
            <Input
              type='password'
              label='New Password'
              id='newPassword'
              name='newPassword'
            />
            <Input
              type='password'
              label='Confirm New Password'
              id='confNewPassword'
              name='confNewPassword'
            />
            <button
              type='submit'
              disabled={loading || loggingOut}
              className='w-full rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60'
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
            <button
              type='button'
              onClick={handleLogout}
              disabled={loading || loggingOut}
              className='w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60'
            >
              {loggingOut ? "Logging out..." : "Log out"}
            </button>
          </Form>
        </Formik>
      </div>
    </div>
  );
}
